/**
 * Pure grouping + relative-time helpers for the widget's history list.
 *
 * Kept free of React so the boundaries (what counts as "yesterday", where the
 * 7-day window ends) are unit-testable without rendering anything.
 */
import { formatDayMonth, formatTime, formatWeekdayShort, toDateKey } from '@/lib/dates';
import type { Thread } from '@/api/alfie';

export type ThreadGroupKey = 'pinned' | 'today' | 'yesterday' | 'last7' | 'earlier';

export interface ThreadGroup {
  key: ThreadGroupKey;
  /** Rendered as an 11px uppercase overline. Already in display case. */
  label: string;
  threads: Thread[];
}

const GROUP_LABELS: Record<ThreadGroupKey, string> = {
  pinned: 'PINNED',
  today: 'TODAY',
  yesterday: 'YESTERDAY',
  last7: 'LAST 7 DAYS',
  earlier: 'EARLIER',
};

/** Group order is fixed — pins float above every recency bucket. */
const GROUP_ORDER: ThreadGroupKey[] = ['pinned', 'today', 'yesterday', 'last7', 'earlier'];

function dayKeyOffset(now: Date, days: number): string {
  const d = new Date(now);
  d.setDate(d.getDate() - days);
  return toDateKey(d);
}

/** Newest `last_message_at` first. Undefined timestamps sort last. */
function byRecency(a: Thread, b: Thread): number {
  return (
    new Date(b.last_message_at ?? 0).getTime() - new Date(a.last_message_at ?? 0).getTime()
  );
}

/** Newest pin first. */
function byPinnedAt(a: Thread, b: Thread): number {
  return new Date(b.pinned_at ?? 0).getTime() - new Date(a.pinned_at ?? 0).getTime();
}

/**
 * Bucket `threads` for the history view.
 *
 * A pinned thread appears ONLY in PINNED — it is not duplicated into its
 * recency bucket. Buckets are calendar-day based (not rolling 24h), so a
 * message from 11pm yesterday reads as "Yesterday" at 1am today rather than
 * "Today". Empty groups are dropped.
 */
export function groupThreads(threads: Thread[], now: Date = new Date()): ThreadGroup[] {
  const todayKey = toDateKey(now);
  const yesterdayKey = dayKeyOffset(now, 1);
  // Inclusive floor of the "last 7 days" window: 6 calendar days before today,
  // so TODAY + YESTERDAY + this window span exactly 7 days.
  const weekFloorKey = dayKeyOffset(now, 6);

  const buckets: Record<ThreadGroupKey, Thread[]> = {
    pinned: [],
    today: [],
    yesterday: [],
    last7: [],
    earlier: [],
  };

  for (const thread of threads) {
    if (thread.pinned_at) {
      buckets.pinned.push(thread);
      continue;
    }
    const key = toDateKey(thread.last_message_at);
    if (key === todayKey) buckets.today.push(thread);
    else if (key === yesterdayKey) buckets.yesterday.push(thread);
    else if (key >= weekFloorKey && key < yesterdayKey) buckets.last7.push(thread);
    else buckets.earlier.push(thread);
  }

  buckets.pinned.sort(byPinnedAt);
  for (const key of ['today', 'yesterday', 'last7', 'earlier'] as const) {
    buckets[key].sort(byRecency);
  }

  return GROUP_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
    key,
    label: GROUP_LABELS[key],
    threads: buckets[key],
  }));
}

/**
 * Compact timestamp for a history row: clock time today and yesterday, the
 * weekday within the last week, then day + month. Every branch routes through
 * `lib/dates.ts` (see the date-format drift guard).
 */
export function relativeTime(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const key = toDateKey(date);
  if (key === toDateKey(now)) return formatTime(date);
  if (key === dayKeyOffset(now, 1)) return formatTime(date);
  if (key >= dayKeyOffset(now, 6)) return formatWeekdayShort(date);
  return formatDayMonth(date);
}
